import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { X, Zap, ZapOff, RotateCcw, Image as ImageIcon, Send, Check, ChevronDown, Sparkles, Type } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Premium photo filters using CSS + Canvas
const FILTERS = [
  { id: 'none', name: 'Original', css: '', matrix: null },
  { id: 'vivid', name: 'Vivid', css: 'saturate(1.4) contrast(1.1)', matrix: null },
  { id: 'warm', name: 'Warm', css: 'sepia(0.25) saturate(1.3) brightness(1.05)', matrix: null },
  { id: 'cool', name: 'Cool', css: 'saturate(0.9) brightness(1.05) hue-rotate(15deg)', matrix: null },
  { id: 'golden', name: 'Golden', css: 'sepia(0.35) saturate(1.5) brightness(1.08) contrast(1.05)', matrix: null },
  { id: 'bw', name: 'B&W', css: 'grayscale(1) contrast(1.15)', matrix: null },
  { id: 'noir', name: 'Noir', css: 'grayscale(1) contrast(1.4) brightness(0.9)', matrix: null },
  { id: 'fade', name: 'Fade', css: 'saturate(0.7) brightness(1.15) contrast(0.85)', matrix: null },
  { id: 'bloom', name: 'Bloom', css: 'brightness(1.12) saturate(1.2) contrast(0.95)', matrix: null },
  { id: 'cinematic', name: 'Cinema', css: 'contrast(1.2) saturate(0.85) brightness(0.95) sepia(0.1)', matrix: null },
  { id: 'vintage', name: 'Vintage', css: 'sepia(0.4) saturate(0.8) brightness(1.1) contrast(1.05)', matrix: null },
  { id: 'drama', name: 'Drama', css: 'contrast(1.35) saturate(1.15) brightness(0.92)', matrix: null },
];

export default function CameraPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = location.state?.returnTo || '/home';
  const chatContext = location.state?.chat || null; // {conversationId, onCapture callback info}

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const recordTimerRef = useRef(null);

  const [facing, setFacing] = useState('environment'); // environment | user
  const [flash, setFlash] = useState(false);
  const [activeFilter, setActiveFilter] = useState(FILTERS[0]);
  const [mode, setMode] = useState('post'); // post | story
  const [isRecording, setIsRecording] = useState(false);
  const [recordDuration, setRecordDuration] = useState(0);
  const [capturedMedia, setCapturedMedia] = useState(null); // {type: 'photo'|'video', data: blob/dataUrl, preview}
  const [uploading, setUploading] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  const [caption, setCaption] = useState('');
  const [showCaption, setShowCaption] = useState(false);

  // Start camera
  const startCamera = useCallback(async () => {
    setCameraReady(false);
    // Stop existing stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
    }
    try {
      const constraints = {
        video: {
          facingMode: facing,
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: true,
      };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      // Try to enable flash/torch
      if (flash) {
        const videoTrack = stream.getVideoTracks()[0];
        try { await videoTrack.applyConstraints({ advanced: [{ torch: true }] }); } catch {}
      }
      setCameraReady(true);
    } catch (err) {
      console.error('Camera access failed:', err);
      toast.error('Camera access denied. Please allow camera permissions.');
    }
  }, [facing, flash]);

  useEffect(() => {
    startCamera();
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
      }
      if (recordTimerRef.current) clearInterval(recordTimerRef.current);
    };
  }, [startCamera]);

  // Toggle flash
  const toggleFlash = async () => {
    const newFlash = !flash;
    setFlash(newFlash);
    if (streamRef.current) {
      const videoTrack = streamRef.current.getVideoTracks()[0];
      try { await videoTrack.applyConstraints({ advanced: [{ torch: newFlash }] }); } catch {}
    }
  };

  // Flip camera
  const flipCamera = () => {
    setFacing(f => f === 'environment' ? 'user' : 'environment');
  };

  // Capture photo with high quality + filter applied via canvas
  const capturePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    // Use native video resolution for best quality
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');

    // Apply CSS filter to canvas
    ctx.filter = activeFilter.css || 'none';

    // Mirror if front camera
    if (facing === 'user') {
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
    }

    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    ctx.setTransform(1, 0, 0, 1, 0, 0); // Reset transform

    // Convert to high-quality blob
    canvas.toBlob((blob) => {
      const preview = canvas.toDataURL('image/jpeg', 0.92);
      setCapturedMedia({ type: 'photo', blob, preview });
    }, 'image/jpeg', 0.95);
  };

  // Start video recording
  const startRecording = () => {
    if (!streamRef.current) return;
    chunksRef.current = [];
    setRecordDuration(0);

    const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp9,opus')
      ? 'video/webm;codecs=vp9,opus'
      : MediaRecorder.isTypeSupported('video/webm')
        ? 'video/webm'
        : 'video/mp4';

    const recorder = new MediaRecorder(streamRef.current, { mimeType, videoBitsPerSecond: 5000000 });
    mediaRecorderRef.current = recorder;

    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };

    recorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: recorder.mimeType });
      const preview = URL.createObjectURL(blob);
      setCapturedMedia({ type: 'video', blob, preview, mimeType: recorder.mimeType });
    };

    recorder.start(100);
    setIsRecording(true);

    recordTimerRef.current = setInterval(() => {
      setRecordDuration(prev => prev + 1);
    }, 1000);
  };

  // Stop video recording
  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    if (recordTimerRef.current) clearInterval(recordTimerRef.current);
    setIsRecording(false);
  };

  // Handle capture button - tap for photo, hold for video
  const pressTimerRef = useRef(null);
  const isLongPressRef = useRef(false);

  const handlePointerDown = () => {
    isLongPressRef.current = false;
    pressTimerRef.current = setTimeout(() => {
      isLongPressRef.current = true;
      startRecording();
    }, 400);
  };

  const handlePointerUp = () => {
    if (pressTimerRef.current) clearTimeout(pressTimerRef.current);
    if (isLongPressRef.current) {
      stopRecording();
    } else {
      capturePhoto();
    }
  };

  // Retake
  const handleRetake = () => {
    if (capturedMedia?.preview && capturedMedia.type === 'video') {
      URL.revokeObjectURL(capturedMedia.preview);
    }
    setCapturedMedia(null);
    setCaption('');
    setShowCaption(false);
  };

  // Upload and send/post
  const handleConfirm = async () => {
    if (!capturedMedia) return;
    setUploading(true);

    const token = localStorage.getItem('dharmbook_token');
    const headers = { Authorization: `Bearer ${token}` };

    try {
      const formData = new FormData();
      const ext = capturedMedia.type === 'photo' ? 'jpg' : 'webm';
      const mimeType = capturedMedia.type === 'photo' ? 'image/jpeg' : (capturedMedia.mimeType || 'video/webm');
      formData.append('file', capturedMedia.blob, `camera_${Date.now()}.${ext}`);

      // Upload
      let uploadUrl = `${API}/upload`;
      if (capturedMedia.type === 'video') {
        // Use media upload for video
        uploadUrl = `${API}/media/upload`;
      }
      const uploadRes = await axios.post(uploadUrl, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });

      const mediaUrl = uploadRes.data.url || uploadRes.data.media_url;
      const mediaType = capturedMedia.type === 'photo' ? 'image' : 'video';

      if (chatContext?.conversationId) {
        // Send to chat
        await axios.post(`${API}/messages/send`, {
          conversation_id: chatContext.conversationId,
          text: caption || '',
          media_url: mediaUrl,
          media_type: mediaType,
        }, { headers });
        toast.success('Sent!');
        navigate(-1);
      } else {
        // Navigate to create post with media
        navigate('/create-post', {
          state: {
            cameraMedia: { url: mediaUrl, type: mediaType, caption },
          },
        });
      }
    } catch (err) {
      console.error('Upload error:', err);
      toast.error('Upload failed. Please try again.');
    }
    setUploading(false);
  };

  // Open gallery
  const galleryRef = useRef(null);
  const handleGallery = () => {
    galleryRef.current?.click();
  };

  const handleGallerySelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const isVideo = file.type.startsWith('video/');
    const preview = URL.createObjectURL(file);
    setCapturedMedia({ type: isVideo ? 'video' : 'photo', blob: file, preview, mimeType: file.type });
  };

  const formatDuration = (s) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;

  // -- RENDER --

  // Preview/Confirm Screen
  if (capturedMedia) {
    return (
      <div className="fixed inset-0 bg-black z-[100] flex flex-col" data-testid="camera-preview">
        {/* Top bar */}
        <div className="flex items-center justify-between px-4 py-3 bg-black/80 backdrop-blur-sm z-10">
          <button onClick={handleRetake} className="text-white font-semibold text-sm flex items-center gap-1.5" data-testid="retake-btn">
            <RotateCcw className="w-5 h-5" /> Retake
          </button>
          <span className="text-white/60 text-xs font-medium uppercase tracking-wider">
            {chatContext ? 'Send to Chat' : mode === 'story' ? 'Story' : 'Post'}
          </span>
          <button
            onClick={handleConfirm}
            disabled={uploading}
            className="bg-[#FF9933] text-white px-5 py-2 rounded-full font-semibold text-sm flex items-center gap-1.5 shadow-lg disabled:opacity-60"
            data-testid="confirm-btn"
          >
            {uploading ? (
              <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
            ) : chatContext ? (
              <><Send className="w-4 h-4" /> Send</>
            ) : (
              <><Check className="w-4 h-4" /> Next</>
            )}
          </button>
        </div>

        {/* Media Preview */}
        <div className="flex-1 flex items-center justify-center bg-black overflow-hidden relative">
          {capturedMedia.type === 'photo' ? (
            <img src={capturedMedia.preview} alt="Captured" className="max-w-full max-h-full object-contain" />
          ) : (
            <video
              src={capturedMedia.preview}
              className="max-w-full max-h-full object-contain"
              controls
              autoPlay
              loop
              playsInline
            />
          )}

          {/* Caption overlay */}
          {showCaption && (
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
              <input
                type="text"
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                placeholder="Add a caption..."
                className="w-full bg-white/15 backdrop-blur-md text-white placeholder:text-white/50 px-4 py-3 rounded-xl text-sm outline-none border border-white/20"
                data-testid="caption-input"
              />
            </div>
          )}
        </div>

        {/* Bottom tools */}
        <div className="flex items-center justify-center gap-6 py-4 bg-black/80">
          <button
            onClick={() => setShowCaption(!showCaption)}
            className={`p-3 rounded-full transition-colors ${showCaption ? 'bg-white/20' : 'bg-white/10 hover:bg-white/15'}`}
            data-testid="caption-toggle"
          >
            <Type className="w-5 h-5 text-white" />
          </button>
        </div>
      </div>
    );
  }

  // Camera Viewfinder
  return (
    <div className="fixed inset-0 bg-black z-[100] flex flex-col" data-testid="camera-page">
      <input type="file" ref={galleryRef} accept="image/*,video/*" className="hidden" onChange={handleGallerySelect} />
      <canvas ref={canvasRef} className="hidden" />

      {/* Top controls */}
      <div className="absolute top-0 left-0 right-0 z-20 flex items-center justify-between px-4 py-3 bg-gradient-to-b from-black/60 to-transparent">
        <button
          onClick={() => navigate(returnTo)}
          className="w-10 h-10 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center"
          data-testid="camera-close-btn"
        >
          <X className="w-6 h-6 text-white" />
        </button>

        <button
          onClick={toggleFlash}
          className="w-10 h-10 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center"
          data-testid="flash-btn"
        >
          {flash ? <Zap className="w-5 h-5 text-yellow-400" /> : <ZapOff className="w-5 h-5 text-white" />}
        </button>

        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`w-10 h-10 rounded-full backdrop-blur-sm flex items-center justify-center ${showFilters ? 'bg-[#FF9933]/60' : 'bg-black/40'}`}
          data-testid="filters-toggle-btn"
        >
          <Sparkles className="w-5 h-5 text-white" />
        </button>
      </div>

      {/* Camera viewfinder */}
      <div className="flex-1 relative overflow-hidden">
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className="absolute inset-0 w-full h-full object-cover"
          style={{
            filter: activeFilter.css || 'none',
            transform: facing === 'user' ? 'scaleX(-1)' : 'none',
          }}
        />

        {!cameraReady && (
          <div className="absolute inset-0 flex items-center justify-center bg-black">
            <div className="text-center">
              <div className="w-12 h-12 border-3 border-white/30 border-t-white rounded-full animate-spin mx-auto mb-3" />
              <p className="text-white/60 text-sm">Starting camera...</p>
            </div>
          </div>
        )}

        {/* Recording indicator */}
        {isRecording && (
          <div className="absolute top-16 left-1/2 -translate-x-1/2 bg-red-600/90 backdrop-blur-sm px-4 py-1.5 rounded-full flex items-center gap-2 z-20">
            <div className="w-2.5 h-2.5 rounded-full bg-white animate-pulse" />
            <span className="text-white text-sm font-mono font-semibold">{formatDuration(recordDuration)}</span>
          </div>
        )}

        {/* Left side tools */}
        <div className="absolute left-4 top-1/2 -translate-y-1/2 flex flex-col gap-4 z-10">
          <button className="w-10 h-10 rounded-full bg-black/30 backdrop-blur-sm flex items-center justify-center" data-testid="text-tool">
            <span className="text-white font-bold text-lg">Aa</span>
          </button>
        </div>
      </div>

      {/* Bottom section */}
      <div className="bg-black">
        {/* Filter strip */}
        {showFilters && (
          <div className="overflow-x-auto scrollbar-hide px-2 py-3" data-testid="filter-strip">
            <div className="flex items-center gap-3 min-w-max px-2">
              {FILTERS.map((f) => (
                <button
                  key={f.id}
                  onClick={() => setActiveFilter(f)}
                  className={`flex flex-col items-center gap-1.5 transition-all ${activeFilter.id === f.id ? 'scale-110' : 'opacity-70 hover:opacity-100'}`}
                  data-testid={`filter-${f.id}`}
                >
                  <div
                    className={`w-14 h-14 rounded-xl overflow-hidden border-2 transition-colors ${
                      activeFilter.id === f.id ? 'border-[#FF9933]' : 'border-white/20'
                    }`}
                  >
                    <div
                      className="w-full h-full bg-gradient-to-br from-orange-200 via-amber-100 to-rose-200"
                      style={{ filter: f.css || 'none' }}
                    />
                  </div>
                  <span className={`text-[10px] font-medium ${activeFilter.id === f.id ? 'text-[#FF9933]' : 'text-white/60'}`}>
                    {f.name}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Capture area */}
        <div className="flex items-center justify-between px-6 pb-4 pt-2">
          {/* Gallery */}
          <button
            onClick={handleGallery}
            className="w-12 h-12 rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center overflow-hidden"
            data-testid="gallery-btn"
          >
            <ImageIcon className="w-6 h-6 text-white" />
          </button>

          {/* Capture button */}
          <div className="relative">
            {isRecording && (
              <div className="absolute inset-[-6px] rounded-full border-[3px] border-red-500 animate-pulse" />
            )}
            <button
              onPointerDown={handlePointerDown}
              onPointerUp={handlePointerUp}
              onPointerLeave={() => { if (pressTimerRef.current) clearTimeout(pressTimerRef.current); if (isRecording) stopRecording(); }}
              className={`w-[72px] h-[72px] rounded-full border-[4px] transition-all ${
                isRecording
                  ? 'border-red-500 bg-red-500/30 scale-110'
                  : 'border-white bg-white/10 hover:bg-white/20 active:scale-95'
              }`}
              data-testid="capture-btn"
            />
          </div>

          {/* Flip camera */}
          <button
            onClick={flipCamera}
            className="w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center"
            data-testid="flip-camera-btn"
          >
            <RotateCcw className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* Mode tabs */}
        <div className="flex items-center justify-center gap-6 pb-6 pt-1">
          {['post', 'story'].map((m) => (
            <button
              key={m}
              onClick={() => setMode(m)}
              className={`text-sm font-bold uppercase tracking-wider transition-colors pb-1 ${
                mode === m ? 'text-white border-b-2 border-[#FF9933]' : 'text-white/40'
              }`}
              data-testid={`mode-${m}`}
            >
              {m}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
